// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * View builders — the persistent shell, the lobby / pre-join screen and the
 * in-call layout. Pure HTML string builders + a couple of small DOM helpers;
 * all wiring lives in main.ts.
 */
import { icon } from './icons.ts';
import type { TalkMode } from './types.ts';

export function shellHtml(): string {
  return `
    <div class="app-shell">
      <header class="topbar">
        <a class="brand" href="./" aria-label="Talkwell home">
          <span class="brand-mark">${icon('radio')}</span>
          <span class="brand-name">Talkwell</span>
        </a>
        <nav class="topnav">
          <button type="button" class="nav-btn" data-modal="how">${icon('help')}<span>How it works</span></button>
          <button type="button" class="nav-btn" data-modal="privacy">${icon('shield')}<span>Privacy</span></button>
          <button type="button" class="nav-btn" data-modal="about">${icon('info')}<span>About</span></button>
          <button type="button" class="nav-btn icon-only" id="log-toggle" aria-label="Event log" title="Event log">${icon('logDrawer')}</button>
        </nav>
      </header>
      <main class="main-content" id="view"></main>
      <footer class="site-footer">
        <div class="footer-inner">
          <span class="privacy-tag glossary-link" data-term="end-to-end encrypted" tabindex="0" role="button">${icon('shield')} Runs entirely in your browser · nothing hosted</span>
          <span class="foot-credit">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
        </div>
      </footer>
    </div>
    <aside class="drawer" id="drawer" hidden aria-label="Event log"></aside>`;
}

export interface LobbyOpts {
  mode: 'create' | 'join';
  room: string;
  name: string;
  talkMode: TalkMode;
}

export function lobbyHtml(o: LobbyOpts): string {
  const heading = o.mode === 'create' ? 'Start a private call' : 'Join the call';
  const sub =
    o.mode === 'create'
      ? 'A link is the whole thing. Send it to whoever you want on the call — no account, no download, and no server ever carries your voice.'
      : `You’ve been invited to room <code>${escapeHtml(o.room)}</code>. Check your mic, then join.`;
  const cta = o.mode === 'create' ? 'Start call' : 'Join call';
  return `
    <section class="lobby">
      <div class="lobby-preview">
        <div class="mic-check">
          <div class="mic-orb" id="mic-orb">${icon('mic')}</div>
          <div class="mic-meter" aria-hidden="true"><div class="mic-meter-fill" id="mic-meter-fill"></div></div>
          <p class="mic-hint" id="mic-hint">Checking your microphone…</p>
        </div>
        <label class="field">
          <span>${icon('mic')} Microphone</span>
          <select id="mic-select"><option value="">Default microphone</option></select>
        </label>
        <p class="perm-note" id="perm-note" hidden></p>
        <p class="ios-note" id="ios-note" hidden>${icon('headphones')} <span>On iPhone, live-call audio comes out of the earpiece — hold it to your ear like a phone call, or pop in headphones. That’s iOS, not a fault.</span></p>
      </div>

      <div class="lobby-form">
        <h1>${heading}</h1>
        <p class="lead">${sub}</p>
        <label class="field">
          <span>Your name</span>
          <input type="text" id="name-input" maxlength="40" placeholder="e.g. Alex" value="${escapeHtml(o.name)}" autocomplete="name" />
        </label>

        <div class="mode-toggle" role="radiogroup" aria-label="Talk mode">
          <button type="button" class="mode-opt ${o.talkMode === 'open' ? 'on' : ''}" data-mode="open" role="radio" aria-checked="${o.talkMode === 'open'}">
            ${icon('mic')}<span class="mode-name">Open mic</span><span class="mode-desc">Everyone’s live, like a normal call</span>
          </button>
          <button type="button" class="mode-opt ${o.talkMode === 'ptt' ? 'on' : ''}" data-mode="ptt" role="radio" aria-checked="${o.talkMode === 'ptt'}">
            ${icon('radio')}<span class="mode-name">Walkie-talkie</span><span class="mode-desc">Hold to talk (or Space). Bandwidth-light.</span>
          </button>
        </div>

        <button type="button" class="btn-primary btn-lg" id="join-btn">${icon('radio')} ${cta}</button>
        <p class="trust-line glossary-link" data-term="full mesh" tabindex="0" role="button">
          ${icon('shield')} End-to-end encrypted · peer-to-peer · nothing touches a server
        </p>
        ${
          o.mode === 'create'
            ? `<button type="button" class="link-btn" id="join-instead">or join a call with a code</button>`
            : `<button type="button" class="link-btn" id="start-instead">or start your own call</button>`
        }
      </div>
    </section>`;
}

export function callHtml(mode: TalkMode): string {
  return `
    <section class="call">
      <div class="stage">
        <div class="sas-bar" id="sas-bar" hidden>
          <span class="sas-label">${icon('shield')}<span class="sas-label-text">Verify aloud</span></span>
          <span class="sas-words" id="sas-words"></span>
          <button type="button" class="icon-btn tiny" data-modal="privacy" title="What is this?">${icon('help')}</button>
        </div>
        <div class="speaker-grid" id="grid"></div>
        <p class="ios-inline" id="ios-inline" hidden>${icon('headphones')} <span>Audio plays from the earpiece on iPhone — hold to your ear, or use headphones.</span></p>
      </div>

      <aside class="sidepanel" id="sidepanel" hidden>
        <div class="side-tabs">
          <button type="button" class="side-tab on" data-side="people">${icon('users')} People <span id="people-count">1</span></button>
          <button type="button" class="side-tab" data-side="chat">${icon('chat')} Chat</button>
          <button type="button" class="icon-btn side-close" id="side-close" aria-label="Close panel">${icon('x')}</button>
        </div>
        <div class="side-body" id="side-people"></div>
        <div class="side-body" id="side-chat" hidden>
          <div class="chat-log" id="chat-log"></div>
          <form class="chat-form" id="chat-form">
            <input type="text" id="chat-input" placeholder="Message everyone…" maxlength="500" autocomplete="off" />
            <button type="submit" class="icon-btn" aria-label="Send">${icon('send')}</button>
          </form>
        </div>
      </aside>

      <div class="controls" id="controls">
        <div class="ctl-left">
          <span class="call-timer" id="call-timer">00:00</span>
          <button type="button" class="ctl-btn ghost" id="invite-btn">${icon('link')}<span>Invite</span></button>
        </div>
        <div class="ctl-center">
          <button type="button" class="talk-btn ${mode === 'ptt' ? 'ptt' : ''}" id="talk-btn" aria-pressed="false">
            <span class="talk-icon">${icon('mic')}</span>
            <span class="talk-label" id="talk-label">${mode === 'ptt' ? 'Hold to talk' : 'Live'}</span>
          </button>
        </div>
        <div class="ctl-right">
          <button type="button" class="ctl-btn ghost" id="btn-mode" title="Switch talk mode">${icon('waves')}<span id="mode-btn-label">${mode === 'ptt' ? 'Open mic' : 'Walkie'}</span></button>
          <button type="button" class="ctl-btn" id="btn-record">${icon('record')}<span>Record</span></button>
          <button type="button" class="ctl-btn ghost" id="btn-people">${icon('users')}<span>People</span></button>
          <button type="button" class="ctl-btn ghost" id="btn-chat">${icon('chat')}<span>Chat</span></button>
          <button type="button" class="ctl-btn danger" id="btn-leave">${icon('hangup')}<span>Leave</span></button>
        </div>
      </div>
    </section>`;
}

/** Build a speaker card element for a participant (or self). */
export function makeCard(id: string, label: string, self: boolean): HTMLElement {
  const el = document.createElement('div');
  el.className = 'speaker';
  el.dataset.peer = id;
  el.style.setProperty('--level', '0');
  el.innerHTML = `
    <div class="orb">
      <div class="orb-ring"></div>
      <div class="orb-avatar"></div>
      <div class="orb-mic">${icon('mic')}</div>
    </div>
    <div class="speaker-name"></div>
    <div class="speaker-state" hidden></div>`;
  (el.querySelector('.orb-avatar') as HTMLElement).textContent = initials(label);
  (el.querySelector('.speaker-name') as HTMLElement).textContent = self ? `${label} (you)` : label;
  return el;
}

export function initials(name: string): string {
  const parts = (name || 'Guest').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || 'G';
}

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
